<script lang="ts">
import AppSelect from '@/components/v2/AppSelect.vue';

export default {
  name: 'ReportingUploadModal',
  components: {
    AppSelect,
  },
};

interface Document {
  name: string;
  type: string;
  accountId: number;
  file: File;
  selected: boolean;
  readyForUpload: boolean;
}
</script>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue';
import AppModal from './v2/AppModal.vue';
import { CloudUpload, Trash2 } from '@lucide/vue';
import { SelectOption } from '@/types/components/Select';
import { z, ZodError } from 'zod';
import { DocumentDomain } from '@/types/Document';
import { ReportingDocumentType, reportingService, ReportingTableauType } from '@/services/ReportingService';

// ------
// model
// ------
const open = defineModel<boolean>('open', { required: true });

// ------
// props
// ------
const props = defineProps<{
  selectedCategoryName: string;
}>();

// ------
// emits
// ------
const emit = defineEmits<{
  submit: [];
}>();

// -----
// refs
// -----
const tableauBaseDomain = import.meta.env.VITE_TABLEAU_BASE_DOMAIN;
const selectedUploadType = ref<'tableau' | 'document'>();
const draggingOver = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const documents = ref<Partial<Document>[]>([]);
const reportingTableauTypes = ref<ReportingTableauType[]>([]);
const tableauTypeOptions = ref<SelectOption[]>([]);
const reportingDocumentTypes = ref<ReportingDocumentType[]>([]);
const eligibleDocumentTypes = ref<SelectOption[]>([]);
const submitError = ref('');

const tableauFormFields = reactive({
  name: {
    value: '',
    error: '',
  },
  url: {
    value: '',
    error: '',
  },
  type: {
    value: '',
    error: '',
  },
});

// ---------
// computed
// ---------
const formSubmittable = computed(() => {
  if (selectedUploadType.value === 'tableau') {
    return validateTableauForm(false);
  }
  if (selectedUploadType.value === 'document') {
    return (
      documentsReadyForUpload.value &&
      documents.value.length > 0
    );
  }
  return false;
});

const documentsReadyForUpload = computed(() => {
  return documents.value.length > 0 && documents.value.every((document) => document.readyForUpload === true);
});

// ---------
// watchers
// ---------
watch(open, async (value) => {
  // initialize stuff when modal first opens
  if (value) {
    selectedUploadType.value = undefined;

    // reset tableau form data
    tableauFormFields.name.value = '';
    tableauFormFields.name.error = '';
    tableauFormFields.url.value = '';
    tableauFormFields.url.error = '';
    tableauFormFields.type.value = '';
    tableauFormFields.type.error = '';

    // reset error
    submitError.value = '';

    // reset document form data
    documents.value = [];
  }
});

watch(selectedUploadType, async (value) => {
  if (value === 'tableau') {
      const tableauTypes = await reportingService.getTableauTypes();
      reportingTableauTypes.value = tableauTypes.filter((t) => t.reportingType === props.selectedCategoryName);

    tableauTypeOptions.value = reportingTableauTypes.value.map((t) => ({
      label: t.tableauTypeName,
      value: t.tableauTypeId.toString(),
    }));
  }

  if (value === 'document') {
      const documentTypes = await reportingService.getDocumentTypes();
      if (documentTypes) {
        reportingDocumentTypes.value = documentTypes.filter((dt) => dt.reportingType === props.selectedCategoryName);
      }

    eligibleDocumentTypes.value = reportingDocumentTypes.value.map((dt) => ({
      label: dt.documentTypeName,
      value: dt.documentTypeId.toString(),
    }));
  }
});

// --------
// methods
// --------
function handleFileDragOver(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = true;
}

function handleFileDragLeave() {
  draggingOver.value = false;
}

function handleFileDrop(e: DragEvent) {
  e.preventDefault();
  e.stopPropagation();
  draggingOver.value = false;

  if (e.dataTransfer?.items) {
    // use the DataTransferItemList interface if available
    let droppedDocuments: Partial<Document>[] = [];
    [...e.dataTransfer.items].forEach((item) => {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          if (file.type === '') {
            console.error('file type extension is required');
            return;
          }

          droppedDocuments.push({
            name: file.name,
            file: file,
            type: '',
            readyForUpload: false,
          });
        }
      }
    });
    documents.value = [...documents.value, ...droppedDocuments];
  } else if (e.dataTransfer?.files) {
    // otherwise use the DataTransfer interface
    let droppedDocuments: Partial<Document>[] = [];
    [...e.dataTransfer.files].forEach((file) => {
      if (file.type === '') {
        console.error('file type extension is required');
        return;
      }

      droppedDocuments.push({
        name: file.name,
        file: file,
        type: '',
        readyForUpload: false,
      });
    });
    documents.value = [...documents.value, ...droppedDocuments];
  }
}

function handleFilesSelected(e: Event) {
  const target = e.target as HTMLInputElement;
  if (target.files) {
    let selectedDocuments: Partial<Document>[] = [];
    [...target.files].forEach((file) => {
      if (file.type === '') {
        console.error('file type extension is required');
        return;
      }

      selectedDocuments.push({
        name: file.name,
        type: '',
        file: file,
        readyForUpload: false,
      });
    });
    documents.value = [...documents.value, ...selectedDocuments];
  }

  e.preventDefault();
  e.stopPropagation();
}

function handleSelectFiles() {
  if (fileInput.value) {
    fileInput.value.click();
  }
}

function handleDeleteDocument(index: number) {
  documents.value.splice(index, 1);
}

async function handleSubmit() {
  if (selectedUploadType.value === 'tableau' && validateTableauForm()) {
    try {
      await reportingService.createReportingTableauDashboard({
        name: tableauFormFields.name.value,
        dashboardUrl: tableauFormFields.url.value,
        tableauTypeId: Number(tableauFormFields.type.value),
        reportingType: props.selectedCategoryName,
      });
      emit('submit');
      open.value = false;
    } catch (e) {
      const cause = e instanceof Error ? e.cause : undefined;
      const msg = (cause as Record<string, any>)?.data?.error ?? (e instanceof Error ? e.message : e);
      submitError.value = String(msg);
      // A 4xx is the user's input, already shown in submitError; console.error would send it to Sentry.
      const status = (cause as Record<string, any>)?.status;
      if (!(status >= 400 && status < 500)) {
        console.error('Failed to create tableau dashboard:', msg);
      }
    }
    return;
  } else if (selectedUploadType.value === 'document' && documentsReadyForUpload.value) {
    const requests: Promise<void>[] = [];

    for (const document of documents.value) {

        requests.push(
          reportingService.createReportingDocument({
            domain: DocumentDomain.REPORTING,
            type: document.type!,
            displayName: document.name || '',
            fileName: document.name || '',
            document: document.file as File,
            reportingType: props.selectedCategoryName,
          }),
        );
    }

    try {
      await Promise.all(requests);
      emit('submit');
      open.value = false;
    } catch (e) {
      console.error('failed to upload documents.', e);
    }
    return;
  }

  console.error('Form submission failed.');
}

// --------------------
// methods: validators
// --------------------
function validateTableauForm(showErrors = true) {
  let isValid = true;

  // name
  try {
    z.string()
      .min(1, { message: 'Name is required' })
      .max(150, { message: 'Name must be less than 150 characters' })
      .parse(tableauFormFields.name.value);
    tableauFormFields.name.error = '';
  } catch (e) {
    if (e instanceof ZodError) {
      if (showErrors) {
        tableauFormFields.name.error = e.errors[0].message;
      }

      isValid = false;
    }
  }

  // url
  try {
    z.string()
      .min(1, { message: 'URL is required' })
      .url({ message: 'Invalid URL' })
      .refine((data) => data.startsWith('https://'), {
        message: "URL must start with 'https://'",
      })
      .refine(
        (data) => {
          if (!tableauBaseDomain) return true; // Skip domain validation if not configured
          try {
            const urlObj = new URL(data);
            return urlObj.hostname.includes(tableauBaseDomain);
          } catch (e) {
            return false;
          }
        },
        {
          message: tableauBaseDomain ? `URL's domain must be ${tableauBaseDomain}` : 'Invalid URL',
        },
      )
      .parse(tableauFormFields.url.value);
    tableauFormFields.url.error = '';
  } catch (e) {
    if (e instanceof ZodError) {
      if (showErrors) {
        tableauFormFields.url.error = e.errors[0].message;
      }

      isValid = false;
    }
  }

  // type
  try {
    z.string().min(1, { message: 'Type is required' }).parse(tableauFormFields.type.value);
    tableauFormFields.type.error = '';
  } catch (e) {
    if (e instanceof ZodError) {
      if (showErrors) {
        tableauFormFields.type.error = e.errors[0].message;
      }

      isValid = false;
    }
  }

  return isValid;
}
</script>

<template>
  <AppModal
    :show="open"
    :show-overflow="true"
    :handle-cancel="() => (open = false)"
    :handle-action="handleSubmit"
    :action-disabled="!formSubmittable">
    <template #content>
      <div v-if="!selectedUploadType" data-testId="upload" class="flex flex-col items-center gap-6">
        <h1 class="text-primary">Choose Upload Type</h1>
        <div class="flex gap-8">
          <button
            v-if="tableauBaseDomain"
            class="btn h-32 w-32 text-primary drop-shadow-sm hover:scale-105"
            @click="selectedUploadType = 'tableau'">
            Tableau Dashboard
          </button>
          <button
            class="btn h-32 w-32 text-primary drop-shadow-sm hover:scale-105"
            @click="selectedUploadType = 'document'">
            Document
          </button>
        </div>
      </div>
      <div v-if="selectedUploadType == 'tableau'" class="flex flex-col gap-2">
        <h1 class="text-primary">Upload Tableau Dashboard</h1>
        <!-- name -->
        <div>
          <label class="label">
            <span class="label-text text-xs text-gray-500">Name</span>
          </label>
          <input
            type="text"
            v-model="tableauFormFields.name.value"
            placeholder="Please enter a name"
            class="input input-sm w-full border border-solid" />
          <span class="text-xs text-red-500">{{ tableauFormFields.name.error }}</span>
        </div>
        <!-- url -->
        <div>
          <label class="label">
            <span class="label-text text-xs text-gray-500">URL</span>
          </label>
          <input
            type="text"
            v-model="tableauFormFields.url.value"
            placeholder="Please enter a valid tableau URL"
            class="input input-sm w-full border border-solid" />
          <span class="text-xs text-red-500">{{ tableauFormFields.url.error }}</span>
        </div>
        <!-- type-->
        <div>
          <label class="label w-1/3">
            <span class="label-text text-xs text-gray-500">Type</span>
          </label>
          <AppSelect
            v-model="tableauFormFields.type.value"
            :options="
              tableauTypeOptions.map((type) => ({
                label: type.label,
                value: type.value,
              }))
            "
            class="w-full" />
          <span class="text-xs text-red-500">{{ tableauFormFields.type.error }}</span>
        </div>
      </div>

      <!-- upload documents -->

      <div v-if="selectedUploadType === 'document'" class="flex flex-col gap-6">
        <div class="flex flex-col">
          <h1 class="text-primary">Upload Document</h1>
          <p class="px-5 pt-2 text-xs italic">
            Upload documents for the {{ selectedCategoryName }} category.
          </p>
        </div>
        <div
          class="p-4 py-0"
          @dragover="handleFileDragOver"
          @drop="handleFileDrop"
          @dragleave="handleFileDragLeave">
          <div
            class="rounded-lg border-2 border-dashed border-gray-400 p-2 transition-all"
            :class="{ 'bg-blue-100': draggingOver }">
            <div class="flex h-full flex-col items-center justify-center">
              <CloudUpload class="text-gray-400" />
              <div class="text-xs font-bold text-gray-400">Drag and drop files here</div>
              <div class="text-xs text-gray-400">or</div>
              <input ref="fileInput" type="file" multiple class="hidden" @change="handleFilesSelected" />
              <button class="btn btn-secondary btn-xs" @click.stop="handleSelectFiles">Select Files</button>
            </div>
          </div>
        </div>
        <!-- list of selected documents -->
        <div class="flex flex-col gap-2 p-4 py-2">
          <div v-for="(document, index) in documents" :key="index">
            <div
              class="flex cursor-pointer items-center justify-between gap-2 rounded-lg border border-gray-200 p-1 shadow-md transition-all">
              <div class="flex-1 overflow-hidden text-ellipsis text-xs italic text-gray-600">
                {{ document.name }}
              </div>
              <AppSelect
                v-if="eligibleDocumentTypes.length > 0"
                v-model="document.type"
                :options="
                  eligibleDocumentTypes.map((type) => ({
                    label: type.label,
                    value: type.label,
                  })).sort((a, b) => a.label.localeCompare(b.label))
                "
                class="h-8 w-full"
                trigger-class="text-xs font-light"
                menu-class="text-xs font-light"
                @update:modelValue="(value) => { document.type = value; document.readyForUpload = !!value; }"
                placeholder="Select Document Type" />
              <div
                class="group ml-5 flex h-6 w-6 items-center justify-center rounded-lg border border-solid border-gray-400 transition-all hover:border-red-300"
                @click="handleDeleteDocument(index)">
                <Trash2 :size="14" class="text-gray-400 transition-all group-hover:stroke-red-500" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <p v-if="submitError" class="mt-4 text-sm text-red-500">{{ submitError }}</p>
    </template>
  </AppModal>
</template>
